import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, PlusCircle, Loader2 } from "lucide-react";
import {
  adminListOrganizations, adminCreateOrganization,
  formatCommunityPartnerError,
} from "@/services/communityPartnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/**
 * HC-EPIC-004 · Sprint 1-B · HarborWell Community Partner Organizations admin.
 *
 * Route: `/admin/community-partner-organizations`
 * Ratified permissions: `community_partner_organizations.view` (list),
 * `community_partner_organizations.create` (create).
 *
 * Owner directive §6B — minimum controlled interface:
 *   - View list
 *   - Create org (Draft)
 *   - Navigate into detail for per-org lifecycle actions
 *
 * NOT included in this pass (deferred to Sprint 2 by directive):
 *   analytics, charts, performance scores, dashboard tiles.
 */
export default function AdminCommunityPartnerOrganizations() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [needsAuth, setNeedsAuth] = useState(false);
  const [err, setErr] = useState("");

  // Create-org dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [userLimit, setUserLimit] = useState(25);
  const [requireApproval, setRequireApproval] = useState(false);

  const refresh = useCallback(async () => {
    setErr("");
    try {
      const data = await adminListOrganizations();
      setItems(data.items || []);
      setNotEnabled(false);
      setNeedsAuth(false);
    } catch (e) {
      const s = e?.response?.status;
      if (s === 404) setNotEnabled(true);
      else if (s === 401 || s === 403) setNeedsAuth(true);
      else setErr(formatCommunityPartnerError(e, "Unable to load Community Partner organizations."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const submitCreate = async (e) => {
    e.preventDefault();
    if (busy || !name.trim()) return;
    setBusy(true);
    try {
      const created = await adminCreateOrganization({
        name: name.trim(),
        displayName: displayName.trim(),
        contactEmail: contactEmail.trim(),
        userLimit: Number(userLimit) || 25,
        requireApproval,
      });
      toast.success("Organization created in Draft.");
      setDialogOpen(false);
      setName(""); setDisplayName(""); setContactEmail(""); setUserLimit(25); setRequireApproval(false);
      nav(`/admin/community-partner-organizations/${created.id}`);
    } catch (e) {
      toast.error(formatCommunityPartnerError(e, "Unable to create organization."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="p-8 text-slate-500 flex items-center gap-2"
                data-testid="admin-cp-orgs-loading">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading…
    </div>;
  }
  if (notEnabled) {
    return <div className="p-8 max-w-lg" data-testid="admin-cp-orgs-not-enabled">
      <Alert>
        <AlertDescription>
          The Community Partner Portal is not enabled in this environment.
        </AlertDescription>
      </Alert>
    </div>;
  }
  if (needsAuth) {
    return <div className="p-8 max-w-lg" data-testid="admin-cp-orgs-needs-auth">
      <Alert variant="destructive">
        <AlertDescription>
          You do not have permission to view Community Partner organizations.
        </AlertDescription>
      </Alert>
    </div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="admin-cp-orgs-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-full bg-slate-900 text-white p-2">
            <Building2 className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900"
                data-testid="admin-cp-orgs-title">
              Community Partner Organizations
            </h1>
            <p className="text-sm text-slate-500">HarborWell administration</p>
          </div>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="admin-cp-orgs-create-open">
              <PlusCircle className="w-4 h-4 mr-1" /> Create organization
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Community Partner Organization</DialogTitle>
            </DialogHeader>
            <form onSubmit={submitCreate} className="space-y-4"
                  data-testid="admin-cp-orgs-create-form">
              <div>
                <Label htmlFor="cp-name">Legal name</Label>
                <Input id="cp-name" required minLength={2}
                       data-testid="admin-cp-orgs-name-input"
                       value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="cp-display">Display name (optional)</Label>
                <Input id="cp-display"
                       data-testid="admin-cp-orgs-display-input"
                       value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="cp-email">Contact email (optional)</Label>
                <Input id="cp-email" type="email"
                       data-testid="admin-cp-orgs-email-input"
                       value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="cp-limit">Active user limit</Label>
                <Input id="cp-limit" type="number" min={1} max={500}
                       data-testid="admin-cp-orgs-limit-input"
                       value={userLimit} onChange={(e) => setUserLimit(e.target.value)} />
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input type="checkbox"
                       data-testid="admin-cp-orgs-approval-toggle"
                       checked={requireApproval}
                       onChange={(e) => setRequireApproval(e.target.checked)} />
                <span>Require HarborWell approval for every invitation.</span>
              </label>
              <div className="flex justify-end">
                <Button type="submit" disabled={busy}
                        data-testid="admin-cp-orgs-create-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create in Draft"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {err && (
        <Alert variant="destructive" data-testid="admin-cp-orgs-error">
          <AlertDescription>{err}</AlertDescription>
        </Alert>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500 bg-slate-50">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Status</th>
              <th className="p-3">Limit</th>
              <th className="p-3">Approval mode</th>
              <th className="p-3">Created</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="admin-cp-orgs-tbody">
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-400"
                    data-testid="admin-cp-orgs-empty">
                  No Community Partner Organizations yet.
                </td>
              </tr>
            )}
            {items.map((o) => (
              <tr key={o.id} className="border-t border-slate-100"
                  data-testid={`admin-cp-org-row-${o.id}`}>
                <td className="p-3">
                  <div className="font-medium text-slate-900">{o.display_name || o.name}</div>
                  <div className="text-xs text-slate-500">{o.name}</div>
                </td>
                <td className="p-3"><Badge variant="outline">{o.status}</Badge></td>
                <td className="p-3">{o.user_limit ?? "—"}</td>
                <td className="p-3">
                  {o.require_harborwell_approval_for_all_invitations
                    ? "HarborWell approves every invite"
                    : "Direct"}
                </td>
                <td className="p-3 text-slate-500">
                  {o.created_at ? new Date(o.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost"
                          onClick={() => nav(`/admin/community-partner-organizations/${o.id}`)}
                          data-testid={`admin-cp-org-open-${o.id}`}>
                    Manage
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
